import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import afrizioneLogo from '../assets/afrizione-logo.png';
import './Portfolio.css';

const projects = [
  {
    id: 1,
    title: 'Afrizione',
    category: 'app',
    tags: ['Realtime Comms', 'Admin Dashboards', 'In-App Payments'],
    desc: 'All-in-one group and community management system empowering churches, fellowship groups, and social organizations to connect and manage members effortlessly.',
    color: 'fire',
    stats: ['Mobile App', 'Churches & Groups', 'East Africa'],
    url: 'https://app.afrizione.co.ke',
    logo: afrizioneLogo,
  },
  {
    id: 2,
    title: 'LaserGuided Cloud Hub',
    category: 'systems',
    tags: ['Cloud', 'React', 'Node.js'],
    desc: 'Enterprise cloud management and digital services hub designed for high-scale business automation and API integration across East Africa.',
    color: 'ice',
    stats: ['99.9% Uptime', 'Cloud Native', 'Enterprise API'],
  },
  {
    id: 3,
    title: 'PrimeMart',
    category: 'web',
    tags: ['Next.js', 'Stripe', 'PostgreSQL'],
    desc: 'High-performance e-commerce platform with real-time inventory, secure payments, and a custom CMS for product management.',
    color: 'fire',
    stats: ['500+ products', '99.9% uptime', '2s load time'],
  },
  {
    id: 4,
    title: 'HealthTrackPro',
    category: 'app',
    tags: ['Flutter', 'Python', 'AWS'],
    desc: 'Comprehensive health monitoring app for clinics — patient records, appointment booking, telemedicine, and analytics.',
    color: 'ice',
    stats: ['HIPAA compliant', '200+ clinics', 'Multi-platform'],
  },
  {
    id: 5,
    title: 'AgriConnect',
    category: 'systems',
    tags: ['Django', 'React', 'MPESA'],
    desc: 'Agricultural marketplace and management system connecting farmers to buyers, with M-PESA payment integration and crop analytics.',
    color: 'fire',
    stats: ['1,000+ farmers', 'M-PESA integrated', 'Nairobi-based'],
  },
  {
    id: 6,
    title: 'ScholarPath',
    category: 'web',
    tags: ['React', 'Node.js', 'MongoDB'],
    desc: 'University scholarship discovery and application platform with AI-powered matching, document management, and deadline tracking.',
    color: 'ice',
    stats: ['5,000+ scholarships', 'Smart matching', 'Pan-Africa'],
  },
];

const filters = [
  { key: 'all', label: 'All Projects' },
  { key: 'web', label: 'Web Dev' },
  { key: 'systems', label: 'Systems' },
  { key: 'app', label: 'Apps' },
];

export default function Portfolio() {
  const [active, setActive] = useState('all');

  const filtered = active === 'all' ? projects : projects.filter(p => p.category === active);

  return (
    <main className="portfolio-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero__glow" />
        <div className="container page-hero__inner">
          <div className="section-header__tag">Our Work</div>
          <h1 className="page-hero__title">
            Proof of <span className="text-fire">Precision</span>
          </h1>
          <p className="page-hero__desc">
            Every project we ship is a testament to our craft.
            Here's a selection of what we've built for clients across Africa and beyond.
          </p>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="portfolio-section">
        <div className="container">
          {/* Filter Tabs */}
          <div className="portfolio-filters">
            {filters.map(f => (
              <button
                key={f.key}
                className={`portfolio-filter ${active === f.key ? 'portfolio-filter--active' : ''}`}
                onClick={() => setActive(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="portfolio-grid">
            {filtered.map((project) => {
              const CardTag = project.url ? 'a' : 'div';
              const cardProps = project.url
                ? { href: project.url, target: '_blank', rel: 'noopener noreferrer' }
                : {};

              return (
                <CardTag
                  key={project.id}
                  {...cardProps}
                  className={`project-card project-card--${project.color} ${project.url ? 'project-card--link' : ''}`}
                >
                  {/* Mock browser visual */}
                  <div className="project-card__visual">
                    <div className="project-card__browser">
                      <div className="project-card__dots">
                        <span /><span /><span />
                        {project.url && (
                          <span className="project-card__url-inline">
                            {project.url.replace(/^https?:\/\//, '')}
                          </span>
                        )}
                      </div>
                      <div className={`project-card__screen ${project.logo ? 'project-card__screen--logo' : ''}`}>
                        {!project.logo && <div className="project-card__screen-grid" />}
                        {project.logo ? (
                          <div className="project-card__logo-wrapper">
                            <img
                              src={project.logo}
                              alt={`${project.title} Logo`}
                              className="project-card__logo-img"
                            />
                          </div>
                        ) : (
                          <div className="project-card__screen-content">
                            <div className="psc-bar" style={{ width: '70%' }} />
                            <div className="psc-bar" style={{ width: '50%' }} />
                            <div className="psc-grid">
                              <div className="psc-block" /><div className="psc-block" /><div className="psc-block" />
                            </div>
                            <div className="psc-bar" style={{ width: '80%' }} />
                            <div className="psc-bar" style={{ width: '40%' }} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="project-card__body">
                    <div className="project-card__tags">
                      {project.tags.map(t => (
                        <span key={t} className="project-card__tag">{t}</span>
                      ))}
                      {project.url && (
                        <span className="project-card__visit-badge">
                          Visit System ↗
                        </span>
                      )}
                    </div>
                    <h3 className="project-card__title">{project.title}</h3>
                    <p className="project-card__desc">{project.desc}</p>
                    <div className="project-card__stats">
                      {project.stats.map(s => (
                        <div key={s} className="project-card__stat">{s}</div>
                      ))}
                    </div>
                  </div>
                </CardTag>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="cta-banner__glow" />
        <div className="container cta-banner__inner">
          <div>
            <h2 className="cta-banner__title">Your Project Could Be Next</h2>
            <p className="cta-banner__sub">Let's add your success story to this list.</p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn btn--white">Start a Project →</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
